import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { skipToken } from '@reduxjs/toolkit/query'

import { User } from '../../../models/User'
import { EmailAttachmentMetadata } from '../../../models/EmailAttachments'

import { Button } from '../../../components/Button/Button'
import { Spinner } from '../../../components/Spinner/Spinner'

import { useGetEmailQuery } from '../../../store/integrations/integrations'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { DocumentUploadQueueItem, EmailAttachmentToAdd, emailAttachmentQueueKey } from '../hooks'

import { useUnprocessedEmailAttachments } from './hooks'
import { DocumentUploadSource, formatEmailAttachmentDate, formatFileSize, truncateFilenameMiddle } from './utils'

import './EmailAttachmentSuggestions.scss'

const _REMOVE_ANIMATION_MS = 200
const _MAX_CLIP_PX = 300
const _INITIAL_EMAIL_FETCH_LIMIT = 6

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const toEmailAttachmentToAdd = (attachment: EmailAttachmentMetadata, connector: string): EmailAttachmentToAdd => ({
  id: attachment.id,
  connector,
  emailId: attachment.emailExternalId,
  attachmentId: attachment.externalId,
  name: attachment.fileName,
  size: attachment.sizeBytes,
})

const groupByEmail = (items: EmailAttachmentMetadata[]): EmailAttachmentMetadata[][] => {
  const groups = new Map<string, EmailAttachmentMetadata[]>()
  for (const item of items) {
    const group = groups.get(item.emailExternalId) ?? []
    group.push(item)
    groups.set(item.emailExternalId, group)
  }
  return Array.from(groups.values())
}

interface EmailAttachmentSuggestionsProps {
  contact: User | null
  queueItems: DocumentUploadQueueItem[]
  disabled?: boolean
  onAdd: (attachments: EmailAttachmentToAdd[]) => Promise<number>
}

export const EmailAttachmentSuggestions: React.FC<EmailAttachmentSuggestionsProps> = ({ contact, queueItems, disabled = false, onAdd }) => {
  const excludeKeys = useMemo(() => {
    const set = new Set<string>()
    for (const item of queueItems) {
      if (item.source === DocumentUploadSource.EMAIL_ATTACHMENT) {
        set.add(emailAttachmentQueueKey(item.emailId, item.attachmentId))
      }
    }
    return set
  }, [queueItems])

  const { items, connector, isNotConnected, hasData, hasAnyUnprocessedAttachments } = useUnprocessedEmailAttachments(contact?.id ?? null, excludeKeys)

  const { trackingClient } = useTracking()

  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set())
  const inFlightIdsRef = useRef<Set<string>>(new Set())
  const [isExpanded, setIsExpanded] = useState(false)
  const [expandedForContactId, setExpandedForContactId] = useState(contact?.id ?? null)

  if ((contact?.id ?? null) !== expandedForContactId) {
    setExpandedForContactId(contact?.id ?? null)
    setIsExpanded(false)
  }

  const listRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useLayoutEffect(() => {
    if (isExpanded || !listRef.current) {
      return
    }

    setIsOverflowing(listRef.current.scrollHeight > _MAX_CLIP_PX)
  }, [isExpanded, items.length, hasData])

  const shownForContactIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!contact) {
      shownForContactIdRef.current = null
      return
    }
    if (!hasData || !items.length) {
      return
    }
    if (shownForContactIdRef.current === contact.id) {
      return
    }
    shownForContactIdRef.current = contact.id
    trackingClient.track({
      eventName: TrackEvent.EMAIL_ATTACHMENT_SUGGESTIONS_SHOWN,
      eventDetails: { attachmentCount: items.length, connector, contactId: contact.id, context: 'web' },
    })
  }, [contact, hasData, items.length, connector, trackingClient])

  if (isNotConnected) {
    return null
  }

  if (!contact) {
    return (
      <div className='email-attachment-suggestions-resting paragraph-small zocks-text-gray-600 d-flex align-items-center justify-content-center gap-2'>
        <i className='bi bi-envelope' />
        Recent email attachments appear here once you select a client
      </div>
    )
  }

  if (!hasData) {
    return (
      <div className='email-attachment-suggestions-resting paragraph-small zocks-text-gray-600 d-flex align-items-center justify-content-center gap-2'>
        <Spinner size='sm' />
        Looking for attachments in recent emails from {contact.firstName}…
      </div>
    )
  }

  if (!hasAnyUnprocessedAttachments) {
    return (
      <div className='email-attachment-suggestions-resting paragraph-small zocks-text-gray-600 d-flex align-items-center justify-content-center gap-2'>
        <i className='bi bi-envelope-open' />
        No email attachments found for this client yet
      </div>
    )
  }

  const handleAdd = async (attachments: EmailAttachmentMetadata[], trigger: 'single' | 'all') => {
    if (disabled || !connector) {
      return
    }

    const ids = attachments.map((attachment) => attachment.id)

    if (ids.some((id) => inFlightIdsRef.current.has(id))) {
      return
    }
    ids.forEach((id) => inFlightIdsRef.current.add(id))

    try {
      let addedCount: number

      if (prefersReducedMotion()) {
        addedCount = await onAdd(attachments.map((attachment) => toEmailAttachmentToAdd(attachment, connector)))
      } else {
        setPendingIds((prev) => new Set([...prev, ...ids]))
        try {
          await new Promise((resolve) => setTimeout(resolve, _REMOVE_ANIMATION_MS))
          addedCount = await onAdd(attachments.map((attachment) => toEmailAttachmentToAdd(attachment, connector)))
        } finally {
          setPendingIds((prev) => {
            const next = new Set(prev)
            ids.forEach((id) => next.delete(id))
            return next
          })
        }
      }

      if (addedCount > 0) {
        trackingClient.track({
          eventName: TrackEvent.EMAIL_ATTACHMENT_ADDED,
          eventDetails: { attachmentCount: addedCount, trigger, connector, context: 'web' },
        })
      }
    } finally {
      ids.forEach((id) => inFlightIdsRef.current.delete(id))
    }
  }

  if (!items.length && excludeKeys.size === 0) {
    return null
  }

  if (!items.length) {
    return (
      <div className='email-attachment-suggestions-card zocks-border-color-cyan border rounded-3'>
        <EmailAttachmentSuggestionsHeader title='All attachments moved below' count={0} onAddAll={undefined} />
      </div>
    )
  }

  const groups = groupByEmail(items.map((item) => item.attachment))

  const showOverlay = !isExpanded && isOverflowing

  return (
    <div className='email-attachment-suggestions-card zocks-border-color-cyan border rounded-3'>
      <EmailAttachmentSuggestionsHeader
        title={`Email attachments from ${contact.firstName}`}
        count={items.length}
        onAddAll={
          disabled
            ? undefined
            : () =>
                handleAdd(
                  items.map((item) => item.attachment),
                  'all',
                )
        }
      />
      <div className='email-attachment-suggestions-clip' style={!isExpanded ? { maxHeight: _MAX_CLIP_PX, overflow: 'hidden' } : undefined}>
        <div ref={listRef} className='email-attachment-suggestions-list d-flex flex-column gap-2 px-3'>
          {groups.map((group, index) => (
            <EmailAttachmentGroupCard
              key={group[0].emailExternalId}
              attachments={group}
              connector={connector as string}
              pendingIds={pendingIds}
              disabled={disabled}
              shouldFetchEmail={isExpanded || index < _INITIAL_EMAIL_FETCH_LIMIT}
              onAdd={(attachment) => handleAdd([attachment], 'single')}
            />
          ))}
        </div>
        {showOverlay ? (
          <div className='email-attachment-suggestions-list-overlay'>
            <Button variant='grayGhost' className='button-small email-attachment-suggestions-add-button rounded-2' onClick={() => setIsExpanded(true)}>
              Show all attachments
            </Button>
          </div>
        ) : null}
      </div>
      <div className='px-3 zocks-py-0.75 paragraph-extra-small zocks-text-gray-600'>
        From the last 30 days. Already processed and unsupported files aren't shown.
      </div>
    </div>
  )
}

interface EmailAttachmentSuggestionsHeaderProps {
  title: string
  count: number
  onAddAll: (() => void) | undefined
}

const EmailAttachmentSuggestionsHeader: React.FC<EmailAttachmentSuggestionsHeaderProps> = ({ title, count, onAddAll }) => {
  return (
    <div className='email-attachment-suggestions-header px-3 zocks-py-0.75 d-flex align-items-center justify-content-between gap-3'>
      <div className='d-flex align-items-center gap-2'>
        <i className='bi bi-envelope zocks-text-dark-green' />
        <span className='paragraph-extra-small bold text-uppercase zocks-text-dark-green'>{title}</span>
      </div>
      {onAddAll && count > 0 ? (
        <Button variant='grayGhost' className='button-small email-attachment-suggestions-add-button rounded-2' onClick={onAddAll}>
          <i className='bi bi-plus-circle' /> Add all
        </Button>
      ) : null}
    </div>
  )
}

interface EmailAttachmentGroupCardProps {
  attachments: EmailAttachmentMetadata[]
  connector: string
  pendingIds: ReadonlySet<string>
  disabled: boolean
  shouldFetchEmail: boolean
  onAdd: (attachment: EmailAttachmentMetadata) => void
}

const EmailAttachmentGroupCard: React.FC<EmailAttachmentGroupCardProps> = ({ attachments, connector, pendingIds, disabled, shouldFetchEmail, onAdd }) => {
  const accountId = useAppSelector(selectAccountId)

  const { emailExternalId } = attachments[0]

  const { data: email } = useGetEmailQuery(shouldFetchEmail ? { accountId, emailConnector: connector, emailId: emailExternalId } : skipToken)

  const threadLink = email ? `/email/inbox/threads/${email.threadId}` : null
  const subject = email?.subject

  return (
    <div className='email-attachment-suggestions-group bg-white zocks-border-color-gray-400 border rounded-3 d-flex flex-column'>
      {attachments.map((attachment, index) => (
        <EmailAttachmentRow
          key={attachment.id}
          attachment={attachment}
          subject={subject}
          threadLink={threadLink}
          isFirstInGroup={index === 0}
          isPending={pendingIds.has(attachment.id)}
          disabled={disabled}
          onAdd={() => onAdd(attachment)}
        />
      ))}
    </div>
  )
}

interface EmailAttachmentRowProps {
  attachment: EmailAttachmentMetadata
  subject: string | null | undefined
  threadLink: string | null
  isFirstInGroup: boolean
  isPending: boolean
  disabled: boolean
  onAdd: () => void
}

const EmailAttachmentRow: React.FC<EmailAttachmentRowProps> = ({ attachment, subject, threadLink, isFirstInGroup, isPending, disabled, onAdd }) => {
  return (
    <div
      className={classNames('email-attachment-suggestions-row px-3 zocks-py-0.75 d-flex align-items-center justify-content-between gap-3', {
        'zocks-border-color-gray-400 border-top': !isFirstInGroup,
        'email-attachment-suggestions-row-removing': isPending,
      })}
    >
      <div className='d-flex flex-column gap-1 mw-0'>
        <span className='paragraph-medium bold text-truncate mw-0' title={attachment.fileName}>
          {truncateFilenameMiddle(attachment.fileName)}
        </span>
        <span className='paragraph-extra-small zocks-text-gray-700'>
          {subject ? (
            threadLink ? (
              <Link to={threadLink} target='_blank' rel='noopener noreferrer' className='email-attachment-suggestions-subject-link'>
                {subject}
              </Link>
            ) : (
              <span>{subject}</span>
            )
          ) : (
            <span>No subject</span>
          )}
          {' · '}
          {formatEmailAttachmentDate(attachment.createdAt)}
          {attachment.sizeBytes ? ` · ${formatFileSize(attachment.sizeBytes)}` : ''}
        </span>
      </div>
      <Button
        variant='grayGhost'
        className='button-small email-attachment-suggestions-add-button rounded-2 flex-shrink-0'
        disabled={disabled || isPending}
        onClick={onAdd}
      >
        <i className='bi bi-plus-circle' /> Add
      </Button>
    </div>
  )
}
